<template>
    <div>
        <PageHeader>
            {{ $t('producerProductsImport') }}
        </PageHeader>

        <Card :header="$t('producerProductsImportDescription')" :disabled="busy">
            <div class="d-flex gap-3 flex-column">
                <ErrorMessage :value="error" />

                <FormValidator form-group :validator="$v.space" v-slot="slotProps" class="mb-0 flex-grow-1 width-min-300">
                    <MultiselectSpaces
                        v-model="space"
                        :multiple="false"
                        :show-checkboxes="false"
                        :clear-on-select="false"
                        :close-on-select="true"
                        :preserve-search="true"
                        :disabled="busy"
                        :state="slotProps.state"
                        @loading="value => (isSpacesLoading = value)"
                    />
                </FormValidator>

                <template v-if="space">
                    <FormValidator form-group :validator="$v.form.importFile" v-slot="slotProps" class="mb-0 flex-grow-1 width-min-300">
                        <FormFile v-model="form.importFile" :disabled="busy" :state="slotProps.state" accept="application/json,.json" />
                    </FormValidator>

                    <div class="d-flex justify-content-end">
                        <ButtonIcon :loading="busy" :disabled="busy" class="width-min-150" @click="submit()">
                            {{ $t('importFile') }}
                        </ButtonIcon>
                    </div>

                    <FormValidator form-group :label="$t('filterByNewProducts')" :validator="$v.filters.hasNewProducts" class="mb-0">
                        <b-form-radio-group v-model="filters.hasNewProducts" :disabled="busy">
                            <b-form-radio :value="null">{{ $t('allReports') }}</b-form-radio>
                            <b-form-radio :value="true">{{ $t('reportsWithNewProducts') }}</b-form-radio>
                        </b-form-radio-group>
                    </FormValidator>
                </template>
            </div>
        </Card>

        <DataTable
            v-if="space"
            :header="$t('productImport')"
            :fields="importsFields"
            :items="imports"
            :loading="busy"
            :total-rows="importsTotal"
            :current-page.sync="currentPage"
            :per-page.sync="perPage"
            no-sort-reset
        >
            <template #cell(action)="{ item }">
                <ButtonIcon
                    size="sm"
                    variant="danger"
                    hide-label-while-loading
                    :loading="requestImport.getLoading(item.id)"
                    :disabled="!canBeDeleted(item)"
                    @click="deleteImport(item)"
                >
                    {{ $t('delete') }}
                </ButtonIcon>
            </template>

            <template #cell(report)="{ item }">
                <div v-if="item.report" class="d-flex flex-column gap-2">
                    <pre class="bg-soft-secondary border border-dark rounded p-2 mb-0">{{ item.report }}</pre>
                    <ButtonIcon
                        size="sm"
                        variant="primary"
                        icon="download"
                        :loading="isImportDownloading(item)"
                        @click="downloadImportFile(item)"
                    >
                        {{ $t('downloadFile') }}
                    </ButtonIcon>
                </div>
            </template>

            <template #cell(status)="{ item }">
                <b-badge :variant="statusMap[item.status]?.variant || 'dark'">
                    {{ statusMap[item.status]?.label || item.status }}
                </b-badge>
            </template>
        </DataTable>
    </div>
</template>

<script>
    import { required } from 'vuelidate/lib/validators';
    import { serialize as objectToFormData } from 'object-to-formdata';
    import FormFile from '@/components/common/FormFile.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import MultiselectSpaces from '@/components/common/MultiselectSpaces.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import exportService from '@/services/exportService';

    export default {
        name: 'ProducerProductsImport',
        components: {
            FormFile,
            FormValidator,
            MultiselectSpaces,
            DataTable,
        },
        data() {
            return {
                isSpacesLoading: false,
                space: null,
                form: {
                    importFile: null,
                },
                filters: {
                    hasNewProducts: null,
                },
                requestImport: this.$req('producerProduct/Import'),
                requestImports: this.$req('producerProduct/Imports'),

                downloadingIds: [],

                perPage: 10,
                currentPage: 1,
            };
        },
        validations() {
            return {
                space: { required },
                form: {
                    importFile: { required },
                },
                filters: {
                    hasNewProducts: {},
                },
            };
        },
        computed: {
            busy() {
                return this.requestImport.getLoading() || this.requestImports.getLoading();
            },
            error() {
                return this.requestImport.getError() || this.requestImports.getError();
            },
            imports() {
                return this.requestImports.getData()?.items || [];
            },
            importsTotal() {
                return this.requestImports.getData()?.max || 0;
            },
            statusMap() {
                return {
                    0: { label: this.$t('pending'), variant: 'secondary' },
                    1: { label: this.$t('processing'), variant: 'warning' },
                    2: { label: this.$t('processed'), variant: 'success' },
                    3: { label: this.$t('corrupted'), variant: 'danger' },
                };
            },
            importsFields() {
                return [
                    { key: 'id', label: this.$t('id') },
                    { key: 'originalFileName', label: this.$t('name') },
                    { key: 'status', label: this.$t('status') },
                    { key: 'newProductsCount', label: this.$t('newProducts') },
                    { key: 'createdBy', label: this.$t('createdBy'), formatter: value => value?.name },
                    { key: 'createdAt', label: this.$t('createdAt'), formatter: value => (value ? this.$format.dateAndHour(value) : '') },
                    { key: 'processedAt', label: this.$t('processedAt'), formatter: value => (value ? this.$format.dateAndHour(value) : '') },
                    { key: 'report', label: this.$t('report') },
                    { key: 'space', label: this.$t('space'), formatter: value => value?.url },
                    { key: 'action', label: '' },
                ];
            },
            requestParams() {
                return this.form;
            },
            apiParams() {
                return {
                    requestParams: objectToFormData(this.requestParams),
                    urlParams: { spaceId: this.space?.id },
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
            },
            listApiParams() {
                return {
                    urlParams: { spaceId: this.space?.id },
                    requestParams: {
                        page: this.currentPage,
                        perPage: this.perPage,
                        hasNewProducts: this.filters.hasNewProducts,
                    },
                };
            },
        },
        watch: {
            space() {
                this.currentPage = 1;
                this.filters.hasNewProducts = null;
            },
            listApiParams: {
                handler() {
                    this.fetchImports();
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            canBeDeleted(importItem) {
                return importItem.status !== 1;
            },
            submit() {
                this.$v.$touch();
                if (this.$v.$invalid) return;
                this.requestImport.post(this.apiParams).then(response => {
                    this.$toastr.success(this.$t('dataImportedSuccessfully'));
                    this.$v.form.importFile.$reset();
                    this.form.importFile = null;
                    this.imports.push(response);
                });
            },
            deleteImport(importItem) {
                return this.requestImport
                    .id(importItem.id)
                    .delete({
                        urlParams: { spaceId: this.space?.id, id: importItem.id },
                    })
                    .then(() => {
                        this.$toastr.success(this.$t('importDeletedSuccessfully'));
                        this.fetchImports();
                    });
            },
            fetchImports() {
                if (!this.space) return;
                this.requestImports.get(this.listApiParams);
            },
            async downloadImportFile(importItem) {
                const url = `${process.env.VUE_APP_API_URL}/api/v1/spaces/${this.space.id}/producer-products/import/${importItem.id}/download`;

                this.downloadingIds.push(importItem.id);

                try {
                    await exportService.exportDataAuthorized(url, `import-${importItem.id}`, 'json');
                } catch (e) {
                    this.$toastr.error(e.message);
                } finally {
                    this.downloadingIds = this.downloadingIds.filter(id => id !== importItem.id);
                }
            },
            isImportDownloading(importItem) {
                return this.downloadingIds.includes(importItem.id);
            },
        },
    };
</script>
